import {
  blobToObjectUrl,
  fetchAuthenticatedBlob,
  saveOrShareBlob,
} from "../utils/authenticatedBlob";
import apiClient, { extractData } from "./client";

const ALLOWED_EXT = new Set([
  ".jpg",
  ".jpeg",
  ".png",
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
]);

const BLOCKED_EXT = new Set([
  ".exe",
  ".bat",
  ".cmd",
  ".apk",
  ".js",
  ".msi",
]);

export const ATTACHMENT_MAX_FILE_SIZE = 10 * 1024 * 1024;
export const ATTACHMENT_MAX_FILES = 5;

export const ATTACHMENT_ACCEPT =
  ".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx";

export function getFileExtension(name = "") {
  const idx = name.lastIndexOf(".");
  return idx >= 0 ? name.slice(idx).toLowerCase() : "";
}

export function validateAttachmentFile(file) {
  if (!file) return "لم يتم اختيار ملف";
  const ext = getFileExtension(file.name);
  if (BLOCKED_EXT.has(ext)) {
    return `نوع الملف محظور: ${ext}`;
  }
  if (!ALLOWED_EXT.has(ext)) {
    return "نوع الملف غير مسموح. المسموح: jpg, jpeg, png, pdf, doc, docx, xls, xlsx";
  }
  if (file.size > ATTACHMENT_MAX_FILE_SIZE) {
    return "حجم الملف يتجاوز الحد الأقصى (10 ميجابايت)";
  }
  return null;
}

export function validateAttachmentFiles(files = []) {
  if (files.length > ATTACHMENT_MAX_FILES) {
    return `لا يمكن رفع أكثر من ${ATTACHMENT_MAX_FILES} ملفات`;
  }
  for (const file of files) {
    const err = validateAttachmentFile(file);
    if (err) return err;
  }
  return null;
}

export async function listAttachments(entityType, entityId) {
  const response = await apiClient.get(
    `/attachments/${encodeURIComponent(entityType)}/${entityId}`,
  );
  return extractData(response);
}

export async function uploadAttachment(entityType, entityId, file, onProgress) {
  const formData = new FormData();
  formData.append("file", file);

  const response = await apiClient.post(
    `/attachments/upload?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (event) => {
        if (!onProgress || !event.total) return;
        onProgress(Math.round((event.loaded * 100) / event.total));
      },
    },
  );
  return extractData(response);
}

export async function uploadAttachments(entityType, entityId, files, onProgress) {
  if (!files?.length) return [];

  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));

  const response = await apiClient.post(
    `/attachments/upload-many?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (event) => {
        if (!onProgress || !event.total) return;
        onProgress(Math.round((event.loaded * 100) / event.total));
      },
    },
  );
  return extractData(response);
}

export async function deleteAttachment(id) {
  const response = await apiClient.delete(`/attachments/${id}`);
  return extractData(response);
}

/**
 * Download via authenticated blob (uploads/ is not publicly served).
 * On mobile uses Filesystem + Share — <a download> is ignored by Android WebView.
 */
export async function downloadAttachment(id, originalName) {
  const blob = await fetchAuthenticatedBlob(
    `/attachments/download/${id}`,
    "تعذر تحميل الملف",
  );
  await saveOrShareBlob(blob, originalName || "attachment");
}

/**
 * Fetch blob URL for in-app preview (images / PDF).
 * Uses apiClient so demo tokens + 401 refresh work the same as the rest of the app.
 */
export async function getAttachmentBlobUrl(id) {
  const blob = await fetchAuthenticatedBlob(
    `/attachments/download/${id}`,
    "تعذر عرض الملف",
  );
  return blobToObjectUrl(blob);
}

export function resolveAttachmentEntityType(source) {
  switch (source) {
    case "leave":
      return "leave";
    case "permit":
      return "permission";
    case "advance":
      return "advance";
    case "complaint":
      return "complaint";
    case "resignation":
      return "resignation";
    case "penalty":
      return "penalty";
    case "announcement":
      return "announcement";
    default:
      return "other";
  }
}
